// Shared landing-page copy — imported by the server page (static prerender)
// and by the client islands (login card, status footer, inquiry survey).

export type Lang = 'DE' | 'EN' | 'FR' | 'IT'

export type Copy = {
  brand: string
  tagline: string
  navFeatures: string
  navSecurity: string
  navLogin: string
  navRequest: string
  heroEyebrow: string
  heroTitle: string
  heroSub: string
  loginTitle: string
  loginSub: string
  emailLabel: string
  passwordLabel: string
  emailPlaceholder: string
  passwordPlaceholder: string
  login: string
  loggingIn: string
  loginError: string
  forgotLink: string
  secTls: string
  sec2fa: string
  secHost: string
  residenciesEyebrow: string
  residenciesTitle: string
  residenciesIntro: string
  residencies: Array<{ flag: string; name: string; note?: string }>
  featuresEyebrow: string
  featuresTitle: string
  featuresIntro: string
  features: Array<{ icon: string; title: string; body: string }>
  securityEyebrow: string
  securityTitle: string
  securityIntro: string
  security: Array<{ icon: string; head: string; body: string }>
  uspIndependent: string
  uspByForFamily: string
  ctaTitle: string
  ctaBody: string
  ctaButton: string
  inquireTitle: string
  inquireIntro: string
  survey: {
    next: string; back: string; submit: string; sending: string; skip: string
    success: string; errorRetry: string
    q: Array<{ key: string; title: string; hint?: string; type: 'single' | 'multi' | 'text' | 'textarea' | 'number' | 'email' | 'callback'; options?: string[]; required?: boolean; placeholder?: string }>
  }
  statusApi: string
  statusAi: string
  statusPrices: string
  versionLabel: string
}

export const T: Record<Lang, Copy> = {
  DE: {
    brand: 'fuchs',
    tagline: 'Intergenerational Family Office AI',
    navFeatures: 'Funktionen',
    navSecurity: 'Sicherheit',
    navLogin: 'Anmelden',
    navRequest: 'Zugang anfordern',
    heroEyebrow: 'Family Office · Geschlossene Beta',
    heroTitle: 'Vermögensverwaltung für Familien, diskret und strukturiert.',
    heroSub: 'Konsolidiertes Familienvermögen über Generationen und Jurisdiktionen. Unabhängig von Banken und Finanzprodukten. Von einem Family Office für Family Offices.',
    loginTitle: 'Anmelden',
    loginSub: 'Geschlossene Beta',
    emailLabel: 'E-Mail-Adresse',
    passwordLabel: 'Passwort',
    emailPlaceholder: 'name@beispiel.ch',
    passwordPlaceholder: '••••••••',
    login: 'Anmelden',
    loggingIn: 'Wird angemeldet…',
    loginError: 'E-Mail oder Passwort falsch.',
    forgotLink: 'Passwort vergessen?',
    secTls: 'TLS 1.3 — Ende-zu-Ende verschlüsselte Verbindung',
    sec2fa: '2-Faktor-Authentifizierung bei Unterschriften',
    secHost: 'Hosting in der Schweiz · Row-Level-Security',
    residenciesEyebrow: '🇨🇭 · 🇩🇪 · 🇪🇺',
    residenciesTitle: 'Steuerresidenzen',
    residenciesIntro: 'Quellensteuer, Doppelbesteuerungsabkommen und Reporting für mitteleuropäische Familien.',
    residencies: [
      { flag: '🇨🇭', name: 'Schweiz', note: 'Verrechnungssteuer + DBA' },
      { flag: '🇩🇪', name: 'Deutschland', note: 'KESt 26,375 % + RD 1' },
      { flag: '🇦🇹', name: 'Österreich' },
      { flag: '🇫🇷', name: 'Frankreich' },
      { flag: '🇮🇹', name: 'Italien' },
      { flag: '🇱🇺', name: 'Luxemburg' },
      { flag: '🇳🇱', name: 'Niederlande' },
      { flag: '🇪🇺', name: 'Übrige EU' },
    ],
    featuresEyebrow: 'Funktionen',
    featuresTitle: 'Was Fuchs für Sie leistet.',
    featuresIntro: 'Konsolidierte Sicht über das Familienvermögen — mit Reporting, Steuer-Simulationen und signierbaren Dokumenten.',
    features: [
      { icon: '📊', title: 'Konsolidiertes Reporting', body: 'PDF-Reports pro Mitglied oder Gesamtfamilie. Versioniert und signiert.' },
      { icon: '💼', title: 'Multi-Asset-Buch', body: 'Aktien, ETFs, Krypto, Immobilien, Vorsorge, Cash, Schulden — ein Buch.' },
      { icon: '💶', title: 'Dividenden-Engine', body: 'Brutto → Quellensteuer → Rückforderung → Netto. Listen für RD 1 und Verrechnungssteuer.' },
      { icon: '📈', title: 'Performance & Historie', body: 'Monatliche Snapshots pro Inhaber, mehrjähriges CAGR, Benchmark-Vergleiche.' },
      { icon: '⚖️', title: 'Steuer-bewusstes Rebalancing', body: 'Simuliert Umschichtungen netto nach Steuer und DBA-Rückforderung.' },
      { icon: '🤖', title: 'KI-Assistent mit Kontext', body: 'Nick kennt Ihr Portfolio. Konkrete Antworten zu Zinsen, Allokation, Rebalancing.' },
      { icon: '📰', title: 'Personalisierte Nachrichten', body: 'DE- und internationale Finanzquellen, gewichtet pro Nutzer.' },
      { icon: '🔏', title: 'Zertifikats-gehärtetes eSigning', body: 'PAdES-B-LTA + RFC 3161. Multi-Signer, Tresor-Versionierung, Audit-Trail.' },
      { icon: '🗂️', title: 'Dokumente und Tresor', body: 'Versionierter Tresor. Passwort-geschützte Freigabe-Links an Externe.' },
    ],
    securityEyebrow: 'Sicherheit',
    securityTitle: 'Schweizer Hosting, Datenhoheit, Bank-Disziplin.',
    securityIntro: 'Family-Office-Daten sind sensibel. Die Infrastruktur ist entsprechend aufgebaut.',
    security: [
      { icon: '🇨🇭', head: 'Hosting in CH / DE', body: 'Ihre Daten verlassen den EU/CH-Raum nicht. Keine US-Subprocessors.' },
      { icon: '🔐', head: 'Zertifikats-eSigning', body: 'PAdES-B-LTA + RFC 3161. Jede Unterschrift forensisch verifizierbar.' },
      { icon: '🛡️', head: 'Mandanten-Isolation', body: 'Row-Level-Security in Postgres. Teams sehen nur eigene Daten.' },
      { icon: '✉️', head: 'Zwei-Faktor bei Signaturen', body: 'E-Mail-Bestätigung pro Signatur; scrypt-Hashing und Rate-Limiting.' },
      { icon: '📋', head: 'Audit-Logs', body: 'Jede Admin-Aktion protokolliert. Fehler intern, nicht an externe Dienste.' },
      { icon: '✋', head: 'Ausschliesslich auf Einladung', body: 'Kein öffentliches Signup. Zusammenarbeit beginnt mit einem Gespräch.' },
    ],
    uspIndependent: 'Unabhängig von Banken und Finanzprodukten.',
    uspByForFamily: 'Von einem Family Office für Family Offices.',
    ctaTitle: 'Zusammenarbeit anfragen',
    ctaBody: 'Ein paar Fragen — wir melden uns persönlich.',
    ctaButton: 'Zugang anfordern',
    inquireTitle: 'Zusammenarbeit anfragen',
    inquireIntro: 'Ein paar Fragen — wir melden uns persönlich.',
    survey: {
      next: 'Weiter', back: 'Zurück', submit: 'Anfrage senden',
      sending: 'Wird gesendet…', skip: 'Überspringen',
      success: 'Danke. Wir melden uns persönlich.',
      errorRetry: 'Etwas ist schiefgelaufen. Bitte erneut versuchen.',
      q: [
        { key: 'role', title: 'In welcher Rolle fragen Sie an?', hint: 'Alle Fragen optional ausser der E-Mail.', type: 'single', options: ['Privatinvestor / Familienmitglied', 'Family Office Manager', 'Vermögensverwalter / Berater', 'Steuerberater / Anwalt', 'Andere'] },
        { key: 'goals', title: 'Welche Aufgabe möchten Sie lösen?', type: 'textarea', placeholder: 'z. B. Reporting über drei Generationen, Dividenden-Rückforderung.' },
        { key: 'team_size', title: 'Wie viele Personen würden nutzen?', type: 'number', placeholder: '4' },
        { key: 'asset_classes', title: 'Welche Anlageklassen halten Sie?', type: 'multi', options: ['Aktien & ETFs', 'Krypto', 'Immobilien (direkt)', 'Immobilien (Fonds)', 'Vorsorge / 3a / bAV', 'Cash & Anleihen', 'Private Equity', 'Derivate', 'Kunst', 'Sonstiges'] },
        { key: 'tax_regions', title: 'Wo liegen die Steuerresidenzen?', type: 'multi', options: ['🇨🇭 Schweiz', '🇩🇪 Deutschland', '🇦🇹 Österreich', '🇫🇷 Frankreich', '🇮🇹 Italien', '🇱🇺 Luxemburg', '🌍 Andere EU', '🌍 Ausserhalb EU'] },
        { key: 'callback', title: 'Wünschen Sie ein Gespräch?', type: 'callback' },
        { key: 'email', title: 'Ihre E-Mail für unsere Antwort.', hint: 'Pflichtfeld.', type: 'email', required: true, placeholder: 'name@family-office.ch' },
        { key: 'name', title: 'Ihr Name.', type: 'text', placeholder: 'Vor- und Nachname' },
      ],
    },
    statusApi: 'API',
    statusAi: 'KI',
    statusPrices: 'Kurse',
    versionLabel: 'Version',
  },
  EN: {
    brand: 'fuchs',
    tagline: 'Intergenerational Family Office AI',
    navFeatures: 'Features',
    navSecurity: 'Security',
    navLogin: 'Sign in',
    navRequest: 'Request access',
    heroEyebrow: 'Family Office · Closed beta',
    heroTitle: 'Wealth management for families, discreet and structured.',
    heroSub: 'Consolidated family wealth across generations and jurisdictions. Independent of banks and financial products. By a family office, for family offices.',
    loginTitle: 'Sign in',
    loginSub: 'Closed beta',
    emailLabel: 'Email address',
    passwordLabel: 'Password',
    emailPlaceholder: 'name@example.com',
    passwordPlaceholder: '••••••••',
    login: 'Sign in',
    loggingIn: 'Signing in…',
    loginError: 'Invalid email or password.',
    forgotLink: 'Forgot password?',
    secTls: 'TLS 1.3 — end-to-end encrypted connection',
    sec2fa: 'Two-factor authentication on signatures',
    secHost: 'Hosted in Switzerland · row-level security',
    residenciesEyebrow: '🇨🇭 · 🇩🇪 · 🇪🇺',
    residenciesTitle: 'Tax residencies',
    residenciesIntro: 'Withholding tax, double-taxation treaties and reporting for Central European families.',
    residencies: [
      { flag: '🇨🇭', name: 'Switzerland', note: 'WHT + treaty' },
      { flag: '🇩🇪', name: 'Germany', note: 'KESt 26.375 % + RD 1' },
      { flag: '🇦🇹', name: 'Austria' },
      { flag: '🇫🇷', name: 'France' },
      { flag: '🇮🇹', name: 'Italy' },
      { flag: '🇱🇺', name: 'Luxembourg' },
      { flag: '🇳🇱', name: 'Netherlands' },
      { flag: '🇪🇺', name: 'Other EU' },
    ],
    featuresEyebrow: 'Features',
    featuresTitle: 'What Fuchs does for you.',
    featuresIntro: 'A consolidated view of the family book — with reporting, tax-aware simulations and signable documents.',
    features: [
      { icon: '📊', title: 'Consolidated reporting', body: 'PDF reports per member or whole family. Versioned and signed.' },
      { icon: '💼', title: 'Multi-asset book', body: 'Equities, ETFs, crypto, real estate, pensions, cash, debt — one book.' },
      { icon: '💶', title: 'Dividend engine', body: 'Gross → withholding → reclaim → net. Lists for RD 1 and Swiss WHT.' },
      { icon: '📈', title: 'Performance & history', body: 'Monthly snapshots per owner, multi-year CAGR, benchmark comparisons.' },
      { icon: '⚖️', title: 'Tax-aware rebalancing', body: 'Simulates sells and buys net of tax and treaty reclaim.' },
      { icon: '🤖', title: 'AI assistant with context', body: 'Nick knows your portfolio. Concrete answers on rates, allocation, rebalancing.' },
      { icon: '📰', title: 'Personalised news', body: 'German and international finance sources, weighted per user.' },
      { icon: '🔏', title: 'Certificate-hardened eSigning', body: 'PAdES-B-LTA + RFC 3161. Multi-signer, vault versioning, audit trail.' },
      { icon: '🗂️', title: 'Documents and vault', body: 'Versioned vault. Password-protected share links to external parties.' },
    ],
    securityEyebrow: 'Security',
    securityTitle: 'Swiss hosting, data sovereignty, banker discretion.',
    securityIntro: 'Family-office data is sensitive. The infrastructure is built accordingly.',
    security: [
      { icon: '🇨🇭', head: 'Hosted in CH / DE', body: 'Data does not leave the EU/CH perimeter. No US sub-processors.' },
      { icon: '🔐', head: 'Certificate eSigning', body: 'PAdES-B-LTA + RFC 3161. Every signature long-term forensically verifiable.' },
      { icon: '🛡️', head: 'Tenant isolation', body: 'Row-level security in Postgres. Teams see only their own rows.' },
      { icon: '✉️', head: 'Two-factor on signatures', body: 'Email confirmation per signature; scrypt-hashed share links, rate limits.' },
      { icon: '📋', head: 'Audit logs', body: 'Every admin action logged. Errors surface internally, not to external services.' },
      { icon: '✋', head: 'Invitation only', body: 'No public signup. Collaboration starts with a conversation.' },
    ],
    uspIndependent: 'Independent of banks and financial products.',
    uspByForFamily: 'By a family office, for family offices.',
    ctaTitle: 'Start a conversation',
    ctaBody: 'A few questions — we\'ll respond personally.',
    ctaButton: 'Request access',
    inquireTitle: 'Start a conversation',
    inquireIntro: 'A few questions — we\'ll respond personally.',
    survey: {
      next: 'Next', back: 'Back', submit: 'Send inquiry',
      sending: 'Sending…', skip: 'Skip',
      success: 'Thank you. We will reply personally.',
      errorRetry: 'Something went wrong. Please try again.',
      q: [
        { key: 'role', title: 'In what capacity are you inquiring?', hint: 'All questions optional except email.', type: 'single', options: ['Private investor / family member', 'Family office manager', 'Wealth manager / adviser', 'Tax adviser / lawyer', 'Other'] },
        { key: 'goals', title: 'What problem are you hoping to solve?', type: 'textarea', placeholder: 'e.g. Reporting across three generations, dividend reclaim.' },
        { key: 'team_size', title: 'How many people would use it?', type: 'number', placeholder: '4' },
        { key: 'asset_classes', title: 'Which asset classes do you hold?', type: 'multi', options: ['Equities & ETFs', 'Crypto', 'Real estate (direct)', 'Real estate (funds)', 'Pensions / 3a', 'Cash & bonds', 'Private equity', 'Derivatives', 'Art', 'Other'] },
        { key: 'tax_regions', title: 'Where are the tax residencies?', type: 'multi', options: ['🇨🇭 Switzerland', '🇩🇪 Germany', '🇦🇹 Austria', '🇫🇷 France', '🇮🇹 Italy', '🇱🇺 Luxembourg', '🌍 Other EU', '🌍 Outside EU'] },
        { key: 'callback', title: 'Would you prefer a call?', type: 'callback' },
        { key: 'email', title: 'Your email for our reply.', hint: 'Required.', type: 'email', required: true, placeholder: 'name@family-office.com' },
        { key: 'name', title: 'Your name.', type: 'text', placeholder: 'First and last name' },
      ],
    },
    statusApi: 'API',
    statusAi: 'AI',
    statusPrices: 'Prices',
    versionLabel: 'Version',
  },
  FR: null as any,
  IT: null as any,
}
T.FR = T.EN
T.IT = T.EN

// Browser-language detection — DE for German browsers, EN otherwise.
// Client-only; callers must run this after mount (or guard for SSR).
export function detectLang(): Lang {
  if (typeof navigator === 'undefined') return 'DE'
  const nav = (navigator.language || '').toLowerCase()
  return nav.startsWith('de') ? 'DE' : 'EN'
}
